import React, { useState } from 'react';
import { useLogin, useNotify } from 'react-admin';
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import { useThemeMode } from './ThemeModeProvider';

const LoginPage: React.FC = () => {
  const login  = useLogin();
  const notify = useNotify();
  const { resolvedMode } = useThemeMode();
  const isDark = resolvedMode === 'dark';

  const [username,    setUsername]    = useState('');
  const [password,    setPassword]    = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [loading,     setLoading]     = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login({ username, password });
    } catch {
      notify('Invalid credentials', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const bg      = isDark ? '#0D1117' : '#F4F5F7';
  const card    = isDark ? '#161B22' : '#FFFFFF';
  const border  = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.08)';
  const text    = isDark ? '#F1F5F9' : '#0F172A';
  const muted   = isDark ? '#94A3B8' : '#64748B';
  const accent  = isDark ? '#818CF8' : '#5B6AF0';
  const inputBg = isDark ? 'rgba(255,255,255,0.04)' : '#FFFFFF';
  const shadow  = isDark
    ? '0 24px 48px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)'
    : '0 24px 48px rgba(15,23,42,0.12), 0 0 0 1px rgba(15,23,42,0.06)';

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      background: inputBg,
      fontSize: 14,
      height: 48,
      transition: 'box-shadow 150ms ease',
      '& fieldset': { borderColor: border, borderWidth: '1.5px' },
      '&:hover fieldset': { borderColor: muted },
      '&.Mui-focused fieldset': { borderColor: `${accent} !important`, borderWidth: '1.5px !important' },
      '&.Mui-focused': { boxShadow: `0 0 0 3px ${isDark ? 'rgba(129,140,248,0.2)' : 'rgba(91,106,240,0.15)'}` },
      '& input': { color: text, padding: '0 14px', height: '100%' },
      '& .MuiInputAdornment-root svg': { color: muted, fontSize: 20 },
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: bg,
        // Subtle grid pattern
        backgroundImage: isDark
          ? 'radial-gradient(circle at 25% 25%, rgba(129,140,248,0.06) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(34,211,238,0.04) 0%, transparent 50%)'
          : 'radial-gradient(circle at 25% 25%, rgba(91,106,240,0.05) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(6,182,212,0.04) 0%, transparent 50%)',
        p: 2,
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: '100%',
          maxWidth: 400,
          background: card,
          borderRadius: '16px',
          boxShadow: shadow,
          border: `1px solid ${border}`,
          p: '40px 36px 36px',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          animation: 'loginIn 300ms cubic-bezier(0.4,0,0.2,1) both',
          '@keyframes loginIn': {
            from: { opacity: 0, transform: 'translateY(16px) scale(0.98)' },
            to:   { opacity: 1, transform: 'translateY(0) scale(1)' },
          },
        }}
      >
        {/* Logo + title */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mb: 1 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #818CF8 0%, #22D3EE 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(129,140,248,0.4)',
            }}
          >
            <LockOutlinedIcon sx={{ color: '#fff', fontSize: 22 }} />
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: text, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
              openBalena
            </Typography>
            <Typography sx={{ fontSize: 13, color: muted, mt: 0.5, letterSpacing: '-0.005em' }}>
              Sign in to your workspace
            </Typography>
          </Box>
        </Box>

        {/* Username */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: muted, letterSpacing: '-0.005em' }}>
            Username
          </Typography>
          <TextField
            value={username}
            onChange={e => setUsername(e.target.value)}
            placeholder="admin"
            autoComplete="username"
            autoFocus
            required
            variant="outlined"
            sx={inputSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Password */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 500, color: muted, letterSpacing: '-0.005em' }}>
            Password
          </Typography>
          <TextField
            value={password}
            onChange={e => setPassword(e.target.value)}
            type={showPass ? 'text' : 'password'}
            placeholder="••••••••"
            autoComplete="current-password"
            required
            variant="outlined"
            sx={inputSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPass(v => !v)}
                    edge="end"
                    size="small"
                    tabIndex={-1}
                    sx={{ color: muted, mr: -0.5 }}
                  >
                    {showPass ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Submit */}
        <Button
          type="submit"
          fullWidth
          disabled={loading || !username || !password}
          sx={{
            mt: 0.5,
            height: 48,
            borderRadius: '10px',
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            background: loading ? undefined : 'linear-gradient(135deg, #5B6AF0 0%, #818CF8 100%)',
            color: '#fff',
            textTransform: 'none',
            transition: 'all 200ms ease',
            boxShadow: '0 2px 8px rgba(91,106,240,0.35)',
            '&:hover:not(:disabled)': {
              transform: 'translateY(-1px)',
              boxShadow: '0 6px 20px rgba(91,106,240,0.45)',
              filter: 'brightness(1.08)',
            },
            '&:active:not(:disabled)': {
              transform: 'translateY(0)',
            },
            '&:disabled': {
              opacity: 0.5,
              color: '#fff',
            },
          }}
        >
          {loading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Sign in'}
        </Button>

        {/* Footer */}
        <Typography sx={{ fontSize: 11, color: muted, textAlign: 'center', opacity: 0.7 }}>
          openBalena Admin Panel
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginPage;
